import { useCallback } from 'react';
import editTracking from '../utils/editTracking';

// Custom hook for edit tracking
export const useEditTracking = () => {
  // Track field edits
  const trackFieldEdit = useCallback(async (entityType, entityId, fieldName, oldValue, newValue, context = {}) => {
    await editTracking.trackFieldEdit(entityType, entityId, fieldName, oldValue, newValue, context);
  }, []);

  // Track bulk edits
  const trackBulkEdit = useCallback(async (entityType, entityId, changes, context = {}) => {
    await editTracking.trackBulkEdit(entityType, entityId, changes, context);
  }, []);

  // Track patient edits
  const trackPatientEdit = useCallback(async (patientId, fieldName, oldValue, newValue, context = {}) => {
    await editTracking.trackPatientEdit(patientId, fieldName, oldValue, newValue, context);
  }, []);

  // Track tooth chart edits
  const trackToothChartEdit = useCallback(async (chartId, toothNumber, fieldName, oldValue, newValue, context = {}) => {
    await editTracking.trackToothChartEdit(chartId, toothNumber, fieldName, oldValue, newValue, context);
  }, []);

  // Track treatment sheet edits
  const trackSheetEdit = useCallback(async (sheetId, sheetType, fieldName, oldValue, newValue, context = {}) => {
    await editTracking.trackSheetEdit(sheetId, sheetType, fieldName, oldValue, newValue, context);
  }, []);

  // Track review edits
  const trackReviewEdit = useCallback(async (reviewId, fieldName, oldValue, newValue, context = {}) => {
    await editTracking.trackReviewEdit(reviewId, fieldName, oldValue, newValue, context);
  }, []);

  // Track appointment edits
  const trackAppointmentEdit = useCallback(async (appointmentId, fieldName, oldValue, newValue, context = {}) => {
    await editTracking.trackAppointmentEdit(appointmentId, fieldName, oldValue, newValue, context);
  }, []);

  // Track medical record edits
  const trackMedicalRecordEdit = useCallback(async (patientId, recordType, fieldName, oldValue, newValue, context = {}) => {
    await editTracking.trackMedicalRecordEdit(patientId, recordType, fieldName, oldValue, newValue, context);
  }, []);

  // Track file edits
  const trackFileEdit = useCallback(async (entityType, entityId, fileName, operation, oldFile = null, newFile = null, context = {}) => {
    await editTracking.trackFileEdit(entityType, entityId, fileName, operation, oldFile, newFile, context);
  }, []);

  // Track status changes
  const trackStatusChange = useCallback(async (entityType, entityId, oldStatus, newStatus, context = {}) => {
    await editTracking.trackStatusChange(entityType, entityId, oldStatus, newStatus, context);
  }, []);

  // Track deletions
  const trackDeletion = useCallback(async (entityType, entityId, entityData, context = {}) => {
    await editTracking.trackDeletion(entityType, entityId, entityData, context);
  }, []);

  // Track creations
  const trackCreation = useCallback(async (entityType, entityId, entityData, context = {}) => {
    await editTracking.trackCreation(entityType, entityId, entityData, context);
  }, []);

  // Track validation errors
  const trackValidationError = useCallback(async (formName, fieldName, errorMessage, fieldValue, context = {}) => {
    await editTracking.trackValidationError(formName, fieldName, errorMessage, fieldValue, context);
  }, []);

  // Track save operations
  const trackSaveOperation = useCallback(async (entityType, entityId, operation, success, errorMessage = null, context = {}) => {
    await editTracking.trackSaveOperation(entityType, entityId, operation, success, errorMessage, context);
  }, []);

  // Track form submissions
  const trackFormSubmission = useCallback(async (formName, formData, success, errors = [], context = {}) => {
    await editTracking.trackFormSubmission(formName, formData, success, errors, context);
  }, []);

  // Track exports
  const trackExport = useCallback(async (entityType, entityId, exportFormat, recordCount, context = {}) => {
    await editTracking.trackExport(entityType, entityId, exportFormat, recordCount, context);
  }, []);

  // Track imports
  const trackImport = useCallback(async (entityType, fileName, recordCount, successCount, errorCount, context = {}) => {
    await editTracking.trackImport(entityType, fileName, recordCount, successCount, errorCount, context);
  }, []);

  return {
    trackFieldEdit,
    trackBulkEdit,
    trackPatientEdit,
    trackToothChartEdit,
    trackSheetEdit,
    trackReviewEdit,
    trackAppointmentEdit,
    trackMedicalRecordEdit,
    trackFileEdit,
    trackStatusChange,
    trackDeletion,
    trackCreation,
    trackValidationError,
    trackSaveOperation,
    trackFormSubmission,
    trackExport,
    trackImport
  };
};

export default useEditTracking;

